import { useEffect, useState } from "react";
import { Check, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import DeskConfirm from "@/components/crm/DeskConfirm";


export const lotBlocked = (l) => !!l.locked || ["reserve", "vendu"].includes(l.status) || (l.statuts || []).some((s) => s === "reserve" || s === "vendu");
export const lotBlockedTip = (l) => (l.locked ? "Lot verrouillé : déverrouillez-le d'abord." : BLOCKED_TIP);
export const BLOCKED_TIP = "Lot rattaché à une réservation ou une vente active : annulez-la d'abord pour pouvoir supprimer le lot.";

/** Case à cocher 15 px du Desk (sélection multiple des lots). */
export function LotCheckbox({ checked, disabled, onChange, title, testId, indeterminate }) {
  const on = checked || indeterminate;
  return (
    <button type="button" role="checkbox" aria-checked={checked} aria-disabled={disabled || undefined} disabled={disabled} title={disabled ? BLOCKED_TIP : title}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }} className="inline-flex items-center justify-center shrink-0 transition-colors"
      style={{ width: 15, height: 15, borderRadius: 4, border: on ? "none" : "1px solid #3a3a42", background: on ? "#7c5cd0" : "transparent", opacity: disabled ? 0.4 : 1, cursor: disabled ? "not-allowed" : "pointer" }} data-testid={testId}>
      {checked && <Check size={11} color="#fff" strokeWidth={3} />}
      {!checked && indeterminate && <span style={{ width: 7, height: 2, background: "#fff", borderRadius: 1 }} />}
    </button>
  );
}

/** Barre d'action fixe en bas dès qu'un lot est coché. */
export function LotSelectionBar({ count, onClear, onDelete, onReserve = null, reservable = 0 }) {
  if (!count) return null;
  return (
    <div className="fixed flex items-center" style={{ left: "50%", bottom: 18, transform: "translateX(-50%)", zIndex: 65, gap: 14, background: "#1c1730", border: "0.5px solid #2f2650", borderRadius: 9, padding: "10px 14px", boxShadow: "0 10px 28px rgba(0,0,0,.5)", fontFamily: "Tahoma, sans-serif" }} data-testid="lot-selection-bar">
      <span style={{ fontSize: 11.5, color: "#c4b3ea" }} data-testid="lot-selection-count">{count} lot{count > 1 ? "s" : ""} sélectionné{count > 1 ? "s" : ""}</span>
      <button type="button" onClick={onClear} style={{ fontSize: 11, color: "#8b8b93" }} data-testid="lot-selection-clear">Tout désélectionner</button>
      {onReserve && <button type="button" onClick={onReserve} disabled={!reservable} className="inline-flex items-center" style={{ gap: 6, background: "rgba(224,168,46,.14)", border: "0.5px solid #E0A82E", borderRadius: 7, padding: "6px 11px", fontSize: 11, color: "#E0A82E", opacity: reservable ? 1 : .45 }} title={reservable ? "Réserver les lots disponibles sélectionnés" : "Aucun lot disponible dans la sélection"} data-testid="lot-selection-reserve">Réserver ({reservable})</button>}
      <button type="button" onClick={onDelete} className="inline-flex items-center" style={{ gap: 6, background: "#2e1c1c", border: "0.5px solid #5c2d2d", borderRadius: 7, padding: "7px 13px", fontSize: 11, color: "#e79a9a" }} data-testid="lot-selection-delete"><Trash2 size={13} />Supprimer</button>
    </div>
  );
}

/** Modale « Supprimer N lot(s) » — motif obligatoire, archivage + notification admins. */
export default function LotArchiveDialog({ lots, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); }, [lots]);
  useEffect(() => {
    if (!lots?.length) return;
    const h = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [lots, onClose]);
  if (!lots?.length) return null;
  const n = lots.length;
  const run = async (reason) => {
    setBusy(true);
    try {
      await api.post("/crm/lots/archive", { ids: lots.map((l) => l.id), reason });
      toast.deleted("lot", n > 1 ? lots.map((l) => l.numero || l.number).join(", ") : lots[0].numero || lots[0].number || "", { n, participle: "archivé", onUndo: async () => { for (const l of lots) await api.post(`/crm/lots/${l.id}/restore`, {}); window.dispatchEvent(new Event("ledesk:crm-changed")); } });
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); } finally { setBusy(false); }
  };
  return (
    <DeskConfirm open danger withReason reasonPlaceholder="Pourquoi ces lots sont-ils supprimés ? (obligatoire)" title={`Supprimer ${n} lot${n > 1 ? "s" : ""} ?`} confirmLabel="Supprimer" busy={busy} onClose={onClose} onConfirm={(r) => run(r)} testId="lot-archive"
      message="Les lots supprimés sont archivés et restaurables depuis les archives du module Commercial.">
      <div className="flex flex-wrap justify-center" style={{ gap: 5 }} data-testid="lot-archive-list">
        {lots.slice(0, 10).map((l) => <span key={l.id} style={{ background: "#101013", border: "0.5px solid #2a2a30", borderRadius: 5, padding: "3px 8px", fontSize: 10, color: "#c4b3ea" }}>{l.numero || l.number}</span>)}
        {n > 10 && <span style={{ padding: "3px 4px", fontSize: 10, color: "#8b8b93" }}>+ {n - 10}</span>}
      </div>
    </DeskConfirm>
  );
}
